import { Autocomplete, Box, TextField, createFilterOptions } from "@mui/material";
import { useState, useMemo, useEffect, useRef } from "react";
import Translate from "../utils/Translate";
import { getCurrentLocale } from "../utils/dateTimeFormat";
import {
  getPhoneCountries,
  splitPhoneNumber,
  toE164PhoneNumber,
  getRegionCodeForPhoneNumber,
  formatNationalPhoneNumber,
} from "../utils/phoneNumber";

const filterCountries = createFilterOptions({
  stringify: (option) => `${option.name} ${option.regionCode} +${option.callingCode}`,
});

// Phone number input with a separate country selector. The value passed to
// onPhoneNumberChange is in E.164 format (e.g. +46701234567) whenever the input
// can be parsed, otherwise the raw input so that validation can reject it.
// allowedRegionCodes limits the countries that can be selected, e.g. ["SE"].
export default function PhoneNumberField({
  value,
  allowedRegionCodes,
  onPhoneNumberChange,
  onBlur,
  label,
  error,
  helperText,
  disablePortal,
  disabled,
  ...rest
}) {
  const allowedRegionCodesKey = allowedRegionCodes?.join(",");
  const countries = useMemo(
    () =>
      getPhoneCountries(getCurrentLocale()).filter(
        (c) => !allowedRegionCodes || allowedRegionCodes.includes(c.regionCode),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowedRegionCodesKey],
  );
  const isRegionAllowed = (code) => countries.some((c) => c.regionCode === code);

  function splitValue(phoneNumber) {
    // getPhoneCountries puts the default country first, so this is the default
    // country if it's allowed, otherwise the first allowed country
    const fallbackRegionCode = countries[0]?.regionCode;
    const split = splitPhoneNumber(phoneNumber, fallbackRegionCode);
    // A number from a country that isn't allowed is shown as is, so validation can reject it
    return isRegionAllowed(split.regionCode)
      ? split
      : { regionCode: fallbackRegionCode, nationalNumber: phoneNumber ?? "" };
  }

  const [regionCode, setRegionCode] = useState(() => splitValue(value).regionCode);
  const [nationalNumber, setNationalNumber] = useState(() => splitValue(value).nationalNumber);

  // Keeps track of the last value we sent, so we only re-parse when
  // the value is changed from outside (e.g. when another actor is loaded)
  const lastEmittedValueRef = useRef(value);

  useEffect(() => {
    if (value !== lastEmittedValueRef.current) {
      const split = splitValue(value);
      setRegionCode(split.regionCode);
      setNationalNumber(split.nationalNumber);
      lastEmittedValueRef.current = value;
    }
  }, [value]);

  function emitChange(newNationalNumber, newRegionCode) {
    const newValue = toE164PhoneNumber(newNationalNumber, newRegionCode);
    lastEmittedValueRef.current = newValue;
    onPhoneNumberChange(newValue);
  }

  function handleNumberChange(event) {
    const newNationalNumber = event.target.value;
    let newRegionCode = regionCode;
    // If the user types or pastes an international number, switch to that country
    if (newNationalNumber.trim().startsWith("+")) {
      const detectedRegionCode = getRegionCodeForPhoneNumber(newNationalNumber);
      if (detectedRegionCode && isRegionAllowed(detectedRegionCode)) {
        newRegionCode = detectedRegionCode;
        setRegionCode(newRegionCode);
      }
    }
    setNationalNumber(newNationalNumber);
    emitChange(newNationalNumber, newRegionCode);
  }

  function handleRegionChange(newRegionCode) {
    setRegionCode(newRegionCode);
    emitChange(nationalNumber, newRegionCode);
  }

  function handleBlur(event) {
    setNationalNumber(formatNationalPhoneNumber(nationalNumber, regionCode));
    onBlur && onBlur(event);
  }

  return (
    <Box sx={{ display: "flex", gap: "8px", width: "100%", alignItems: "flex-start" }}>
      <Autocomplete
        disableClearable
        disabled={disabled || countries.length <= 1}
        disablePortal={disablePortal}
        options={countries}
        value={countries.find((c) => c.regionCode === regionCode) ?? null}
        onChange={(_, option) => option && handleRegionChange(option.regionCode)}
        // Changing country can make the number invalid, so validate when leaving the selector too
        onBlur={onBlur}
        isOptionEqualToValue={(option, selected) => option.regionCode === selected.regionCode}
        getOptionLabel={(option) => `+${option.callingCode}`}
        filterOptions={filterCountries}
        renderOption={(props, option) => (
          <li {...props} key={option.regionCode}>
            {`${option.name} (${option.regionCode}) +${option.callingCode}`}
          </li>
        )}
        slotProps={{
          paper: { sx: { minWidth: "260px" } },
          popupIndicator: { title: undefined, "aria-label": Translate.get("CountryCode") },
        }}
        sx={{
          width: "100px",
          flexShrink: 0,
          "& .MuiAutocomplete-inputRoot": { paddingRight: "28px !important" },
          "& .MuiAutocomplete-endAdornment": { right: "4px" },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={Translate.get("CountryCode")}
            // Keep the same height as the number field so they line up
            helperText={helperText ? " " : undefined}
          />
        )}
      />
      <TextField
        fullWidth
        type="tel"
        autoComplete="tel-national"
        value={nationalNumber}
        onChange={handleNumberChange}
        onBlur={handleBlur}
        label={label}
        error={error}
        helperText={helperText}
        disabled={disabled}
        {...rest}
      />
    </Box>
  );
}
